/**
 * V1 brand-to-creator matching: score by platform overlap and niche overlap.
 * Fetches a limited set of creator profiles and filters/sorts in memory.
 * TODO: Add indexed queries and server-side filtering for scale.
 */

import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { getConnectedPlatforms } from './social/getConnectedPlatforms';

const USERS_COLLECTION = 'users';
const INFLUENCERS_COLLECTION = 'influencers';
const MAX_FETCH = 200;

/** Primary source for "who is a creator": users collection (userType === 'influencer' or role === 'creator'). */
const CREATOR_USER_TYPES = ['influencer', 'creator'];

export interface BrandPreferences {
  platforms?: string[];
  niches?: string[];
  followerRange?: string;
  budgetRange?: string;
  geography?: string | null;
}

export interface CreatorMatchCandidate {
  id: string;
  displayName?: string;
  username?: string;
  profileImageUrl?: string;
  niches?: string[];
  niche?: string[];
  platforms?: Array<{ platformName?: string; followers?: number }>;
  /** Normalized platform names (e.g. tiktok, instagram) for filtering. */
  platformNames?: string[];
  /** Single-line location string for display and client-side location filter. */
  location?: string;
  stats?: { engagementRate?: number; totalFollowers?: number };
  platformStats?: Record<string, { followers?: number; engagementRate?: number }>;
  score: number;
}

// TODO: If influencer docs gain onboardingCompleted, add where('onboardingCompleted','==',true) to the query.

function normalizePlatform(name: string): string {
  const n = (name || '').toLowerCase();
  if (n === 'tiktok' || n === 'instagram' || n === 'youtube') return n;
  if (n === 'x') return 'twitter';
  return n;
}

function getCreatorPlatforms(creator: Record<string, unknown>): string[] {
  const platforms = creator.platforms as Array<{ platformName?: string }> | undefined;
  if (!Array.isArray(platforms)) return [];
  return platforms
    .map((p) => (p?.platformName && normalizePlatform(p.platformName)) || '')
    .filter(Boolean);
}

/** Split comma-separated strings and trim; always returns a flat array of individual niche strings. */
function normalizeNichesToArray(raw: string[] | string | undefined): string[] {
  if (Array.isArray(raw)) {
    const out: string[] = [];
    for (const item of raw) {
      const s = (item || '').trim();
      if (!s) continue;
      if (s.includes(',')) {
        out.push(...s.split(',').map((n) => n.trim()).filter(Boolean));
      } else {
        out.push(s);
      }
    }
    return out;
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((n) => n.trim()).filter(Boolean);
  }
  return [];
}

function getCreatorNiches(creator: Record<string, unknown>): string[] {
  const niches = normalizeNichesToArray(creator.niches as string[] | string | undefined);
  if (niches.length) return niches;
  const niche = creator.niche as string[] | string | undefined;
  if (Array.isArray(niche) || typeof niche === 'string') return normalizeNichesToArray(niche);
  const identity = creator.identity as Record<string, unknown> | undefined;
  const subtitle = identity?.subtitle as string | undefined;
  if (typeof subtitle === 'string' && subtitle.trim()) return normalizeNichesToArray(subtitle);
  return [];
}

function getCreatorLocation(creator: Record<string, unknown>): string {
  const loc = creator.location as string | undefined;
  if (typeof loc === 'string' && loc.trim()) return loc.trim();
  const identity = creator.identity as Record<string, unknown> | undefined;
  if (identity && typeof identity.location === 'string' && identity.location.trim()) return identity.location.trim();
  if (identity && typeof identity === 'object') {
    const parts = [
      identity.city,
      identity.state,
      identity.country,
    ].filter((v): v is string => typeof v === 'string' && (v as string).trim().length > 0);
    if (parts.length) return parts.join(', ');
  }
  return '';
}

function toNum(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * Total follower count from all known sources: legacy manual input, platformStats (scraped),
 * platforms[].followers, stats by platform, socialAccounts, profile.audience.
 */
function getTotalFollowers(creator: Record<string, unknown>): number {
  const stats = creator.stats as Record<string, unknown> | undefined;
  const identity = creator.identity as Record<string, unknown> | undefined;
  const profile = creator.profile as Record<string, unknown> | undefined;
  const audience = profile?.audience as Record<string, unknown> | undefined;

  const legacy =
    toNum(stats?.totalFollowers) ||
    toNum(stats?.followers) ||
    toNum(identity?.totalFollowersOverride) ||
    toNum(audience?.totalFollowers) ||
    toNum(creator.followers) ||
    toNum(creator.followersCount);

  let fromPlatformStats = 0;
  const platformStats = creator.platformStats as Record<string, { followers?: number; followersCount?: number }> | undefined;
  if (platformStats && typeof platformStats === 'object' && !Array.isArray(platformStats)) {
    for (const p of Object.values(platformStats)) {
      if (p && typeof p === 'object') {
        fromPlatformStats += toNum(p.followers) || toNum(p.followersCount);
      }
    }
  }

  let fromPlatformsArray = 0;
  const platforms = creator.platforms as Array<{ followers?: number }> | undefined;
  if (Array.isArray(platforms)) {
    for (const p of platforms) {
      if (p && typeof p === 'object') fromPlatformsArray += toNum(p.followers);
    }
  }

  const statsByPlatform = stats as Record<string, { followers?: number }> | undefined;
  const fromStatsNested =
    toNum(statsByPlatform?.tiktok?.followers) +
    toNum(statsByPlatform?.instagram?.followers) +
    toNum(statsByPlatform?.youtube?.followers);

  const socialAccounts = creator.socialAccounts as Record<string, { followersCount?: number }> | undefined;
  let fromSocialAccounts = 0;
  if (socialAccounts && typeof socialAccounts === 'object') {
    for (const v of Object.values(socialAccounts)) {
      if (v && typeof v === 'object') fromSocialAccounts += toNum(v.followersCount);
    }
  }

  return Math.max(legacy, fromPlatformStats + fromPlatformsArray, fromStatsNested, fromSocialAccounts);
}

function getEngagementRate(creator: Record<string, unknown>): number {
  const stats = creator.stats as { engagementRate?: number } | undefined;
  if (stats?.engagementRate != null) return Number(stats.engagementRate);
  const platformStats = creator.platformStats as Record<string, { engagementRate?: number }> | undefined;
  if (platformStats && typeof platformStats === 'object') {
    const rates = Object.values(platformStats)
      .map((p) => p?.engagementRate)
      .filter((r): r is number => typeof r === 'number');
    if (rates.length) return rates.reduce((a, b) => a + b, 0) / rates.length;
  }
  return 0;
}

/**
 * Score = (platformOverlap * 2) + nicheOverlap. Sorted by score desc, then engagement rate.
 */
export function scoreCreator(
  creator: Record<string, unknown>,
  preferences: BrandPreferences,
): number {
  const brandPlatforms = new Set((preferences.platforms || []).map(normalizePlatform));
  const brandNiches = new Set(
    (preferences.niches || []).map((n) => (n || '').trim().toLowerCase()).filter(Boolean),
  );
  const creatorPlatforms = getCreatorPlatforms(creator);
  const creatorNiches = getCreatorNiches(creator);

  let platformOverlap = 0;
  for (const p of creatorPlatforms) {
    if (brandPlatforms.has(p)) platformOverlap += 1;
  }
  let nicheOverlap = 0;
  for (const n of creatorNiches) {
    if (brandNiches.has((n || '').trim().toLowerCase())) nicheOverlap += 1;
  }

  return platformOverlap * 2 + nicheOverlap;
}

/**
 * Get username for profile link from a user or influencer doc.
 */
function getUsername(data: Record<string, unknown>): string | undefined {
  const u = (data.username ?? data.handle ?? data.usernameLower) as string | undefined;
  if (typeof u === 'string' && u.trim()) return u.trim();
  const identity = data.identity as Record<string, unknown> | undefined;
  const handle = identity?.handle as string | undefined;
  if (typeof handle === 'string' && handle.trim()) return handle.trim();
  const email = data.email as string | undefined;
  if (typeof email === 'string' && email.includes('@')) return email.split('@')[0].trim();
  return undefined;
}

/**
 * Fetch creator candidates from the users collection (userType influencer/creator or role creator).
 * This is the main list of creators on InfluenceHub. Enriches with influencers/{uid} when present.
 */
export async function getCreatorMatchCandidates(
  preferences: BrandPreferences,
): Promise<CreatorMatchCandidate[]> {
  const usersRef = collection(db, USERS_COLLECTION);
  const [byUserTypeSnap, byRoleSnap] = await Promise.all([
    getDocs(query(usersRef, where('userType', 'in', CREATOR_USER_TYPES), limit(MAX_FETCH))),
    getDocs(query(usersRef, where('role', 'in', CREATOR_USER_TYPES), limit(MAX_FETCH))),
  ]);
  const seen = new Set<string>();
  const docs: { id: string; data: Record<string, unknown> }[] = [];
  for (const d of byUserTypeSnap.docs) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    docs.push({ id: d.id, data: d.data() as Record<string, unknown> });
  }
  for (const d of byRoleSnap.docs) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    docs.push({ id: d.id, data: d.data() as Record<string, unknown> });
  }
  const capped = docs.slice(0, MAX_FETCH);
  const creators: CreatorMatchCandidate[] = [];

  const influencerSnaps = await Promise.all(
    capped.map((d) => getDoc(doc(db, INFLUENCERS_COLLECTION, d.id))),
  );

  capped.forEach((d, i) => {
    const uid = d.id;
    const userData = d.data;
    const infSnap = influencerSnaps[i];
    if (!infSnap?.exists()) return;
    const data = { ...userData, ...(infSnap.data() as Record<string, unknown>) } as Record<string, unknown>;

    const score = scoreCreator(data, preferences);
    const connectedPlatforms = getConnectedPlatforms(data);
    const platformNames = connectedPlatforms.length > 0 ? connectedPlatforms : getCreatorPlatforms(data);
    const username = getUsername(data) || getUsername(userData);
    const totalFollowers = getTotalFollowers(data);

    const rawPlatforms = data.platforms;
    const platformsArray = Array.isArray(rawPlatforms) ? rawPlatforms : undefined;

    const existingStats = (data.stats as CreatorMatchCandidate['stats']) || {};
    creators.push({
      id: uid,
      displayName: (data.displayName ?? data.name ?? userData.displayName ?? userData.name) as string | undefined,
      username,
      profileImageUrl: (data.profileImageUrl ?? data.photoURL ?? data.avatarUrl ?? userData.photoURL) as string | undefined,
      niches: getCreatorNiches(data),
      platforms: platformsArray as CreatorMatchCandidate['platforms'],
      platformNames,
      location: getCreatorLocation(data),
      stats: { ...existingStats, totalFollowers: totalFollowers > 0 ? totalFollowers : existingStats.totalFollowers },
      platformStats: data.platformStats as CreatorMatchCandidate['platformStats'],
      score,
    });
  });

  creators.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const erA = a.stats?.engagementRate ?? 0;
    const erB = b.stats?.engagementRate ?? 0;
    return erB - erA;
  });

  const seenHandles = new Set<string>();
  const deduped = creators.filter((c) => {
    const key = (c.username ?? c.id).toLowerCase().trim();
    if (seenHandles.has(key)) return false;
    seenHandles.add(key);
    return true;
  });

  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development' && deduped.length < creators.length) {
    console.log('[brandMatching] Deduped by username:', creators.length, '->', deduped.length);
  }

  return deduped;
}
